import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slide", "content", "dot", "progressBar", "playPause", "pauseIcon", "playIcon"]
  static values = {
    msgPlay: { type: String, default: "재생" },
    msgPause: { type: String, default: "일시정지" }
  }

  connect() {
    this.current = 0
    this.duration = 5000
    this.manuallyPaused = false
    this.remainingTime = this.duration
    this.slideStartTime = null

    // 첫 슬라이드 즉시 표시 (transition 없이)
    if (this.hasSlideTarget) {
      const first = this.slideTargets[0]
      first.style.transition = 'none'
      first.style.opacity = '1'
      first.style.transform = 'scale(1)'
      requestAnimationFrame(() => { first.style.transition = '' })
    }

    this._syncPlayback()
    this._bindSwipe()
  }

  disconnect() {
    this._clearTimer()
    this._unbindSwipe()
  }

  togglePlay() {
    this.manuallyPaused = !this.manuallyPaused
    this._syncPlayback()
  }

  prev() {
    const total = this.slideTargets.length
    this._navigate((this.current - 1 + total) % total)
    if (!this.manuallyPaused) this._restartAutoplay()
  }

  next() {
    this._navigate((this.current + 1) % this.slideTargets.length)
    if (!this.manuallyPaused) this._restartAutoplay()
  }

  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (index === this.current) return
    this._navigate(index)
    if (!this.manuallyPaused) this._restartAutoplay()
  }

  _navigate(next) {
    const prev = this.current

    this.slideTargets[prev].classList.remove("active")
    this.contentTargets[prev].classList.remove("active")
    this.contentTargets[prev].classList.add("leaving")
    if (this.hasDotTarget) this.dotTargets[prev].classList.remove("active")

    setTimeout(() => {
      this.contentTargets[prev].classList.remove("leaving")
    }, 400)

    this.current = next

    this.slideTargets[next].classList.add("active")
    this.contentTargets[next].classList.add("active")
    if (this.hasDotTarget) {
      this.dotTargets[next].classList.add("active")
      if (this.manuallyPaused) this._setDotPaused(true)
    }

    this._restartProgress()
  }

  _startTimer(delay = this.duration) {
    this._clearTimer()
    this.slideStartTime = Date.now()
    // 첫 발화는 남은 시간(delay) 후, 이후는 매 duration마다
    this.timer = setTimeout(() => {
      this._navigate((this.current + 1) % this.slideTargets.length)
      this.remainingTime = this.duration
      this.timer = setInterval(() => {
        this._navigate((this.current + 1) % this.slideTargets.length)
      }, this.duration)
    }, delay)
  }

  _clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer)
      clearInterval(this.timer)
      this.timer = null
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.animationName = "none"
    }
  }

  _restartAutoplay() {
    this.remainingTime = this.duration
    this.slideStartTime = Date.now()
    this._clearTimer()
    this._startTimer(this.duration)
    this._setDotPaused(false)
  }

  _restartProgress() {
    if (!this.hasProgressBarTarget) return
    const bar = this.progressBarTarget
    bar.style.animationName = "none"
    bar.offsetHeight
    bar.style.animationDuration = this.duration + "ms"
    bar.style.animationName = "hero-progress"
  }

  _setDotPaused(paused) {
    if (!this.hasDotTarget) return
    const dot = this.dotTargets[this.current]
    if (!dot) return
    // CSS animation-play-state로 멈춘 위치 유지 — active 재토글 없음
    paused ? dot.classList.add("dot-paused") : dot.classList.remove("dot-paused")
  }

  _bindSwipe() {
    this._touchStartX = null
    this._onTouchStart = (e) => { this._touchStartX = e.touches[0].clientX }
    this._onTouchEnd = (e) => {
      if (this._touchStartX === null) return
      const dx = e.changedTouches[0].clientX - this._touchStartX
      if (Math.abs(dx) < 40) return
      dx < 0 ? this.next() : this.prev()
    }
    this.element.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.element.addEventListener("touchend", this._onTouchEnd, { passive: true })
  }

  _unbindSwipe() {
    this.element.removeEventListener("touchstart", this._onTouchStart)
    this.element.removeEventListener("touchend", this._onTouchEnd)
  }

  _syncPlayback() {
    if (this.manuallyPaused) {
      // 남은 시간 기록
      if (this.slideStartTime) {
        this.remainingTime = Math.max(0, this.duration - (Date.now() - this.slideStartTime))
      }
      this._clearTimer()
      this._setDotPaused(true)
    } else {
      // 남은 시간부터 재개
      this._startTimer(this.remainingTime)
      this._setDotPaused(false)
    }

    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.style.display = this.manuallyPaused ? "none" : ""
    }
    if (this.hasPlayIconTarget) {
      this.playIconTarget.style.display = this.manuallyPaused ? "" : "none"
    }
    if (this.hasPlayPauseTarget) {
      this.playPauseTarget.setAttribute("aria-label", this.manuallyPaused ? this.msgPlayValue : this.msgPauseValue)
    }
  }
}
